import { InteractionRequiredAuthError, type IPublicClientApplication } from "@azure/msal-browser";
import { apiRequest } from "../config/msal";
import { API_BASE_URL } from "../config/env";

export const API_BASE = API_BASE_URL;

async function getToken(msal: IPublicClientApplication): Promise<string> {
  // The active account is set at startup (main.tsx), on popup sign-in (signIn) and by AuthSync.
  const account = msal.getActiveAccount();
  if (!account) throw new Error("No active account");

  try {
    const result = await msal.acquireTokenSilent({ ...apiRequest, account });
    return result.accessToken;
  } catch (err) {
    // Only an interaction-required failure is recoverable by prompting the user; anything else
    // (network, configuration, MSAL state) propagates unchanged.
    if (!(err instanceof InteractionRequiredAuthError)) throw err;
    const result = await msal.acquireTokenPopup({ ...apiRequest, account });
    return result.accessToken;
  }
}

export function resolveBlobIdUrl(blobId: string): string {
  if (blobId.trim() === "") {
    throw new Error("Blob id is empty");
  }

  return `${API_BASE}/web/storage/blob/${blobId}`;
}

/** Authenticated JSON call. A 204 resolves to `undefined` (deletes); any non-2xx throws. */
export async function apiFetch<T>(
  path: string,
  msal: IPublicClientApplication,
  options?: RequestInit,
): Promise<T> {
  const token = await getToken(msal);
  const isFormData = options?.body instanceof FormData;
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      // omit Content-Type for FormData - browser sets it with the correct multipart boundary
      ...(!isFormData && { "Content-Type": "application/json" }),
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(body === "" ? `HTTP ${response.status}` : `HTTP ${response.status}: ${body}`);
  }

  if (response.status === 204) return undefined as T;
  return await response.json() as T;
}

export function apiPost<T>(path: string, msal: IPublicClientApplication, body: unknown, signal?: AbortSignal): Promise<T> {
  return apiFetch<T>(path, msal, { method: "POST", body: JSON.stringify(body), signal });
}

export function apiDelete(path: string, msal: IPublicClientApplication): Promise<void> {
  return apiFetch<void>(path, msal, { method: "DELETE" });
}
